import { useEffect, useState, useRef } from "react";
import styles from "./TermsCondition.module.scss";
import Swal from "sweetalert2";
import Checkbox from "@mui/material/Checkbox";
import MainHeader from "../../components/Header/MainHeader";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { trackEvent } from "../../lib/google";
import { googleEvent_LOJ167, googleEvent_LOJ170 } from "../../lib/google-analytics";
import { pixelEvents } from "../../lib/pixel-events";
import OtpModal from "../../components/Modal/OtpModal";
import { KFS_OTP } from "../../statusCode/statusCode";
import CustomButton from "../../components/Button/CustomButton";
import { getEnvironment } from "../../utils/environment";
import { clevertap_sv_key_fct_stat, clevertap_key_fct_stat_back_clk, clevertap_key_fct_stat_consent, clevertap_key_fct_stat_accept_clk, clevertap_KFS_pl_screen, clevertap_KFS_pl_screen_click, clevertap__KFS_screen } from "../../lib/clevertap-events";
import { getGeolocation } from "../../utils/utils";
import { useNavigate } from "react-router-dom";

const label = {
  inputProps: {
    "aria-label": "Checkbox",
  },
};

export default function TermsCondition() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [checkTerm, setCheckTerm] = useState(true);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [apiResponseMsg, setApiResponseMsg] = useState();
  const [resendTrigger, setResendTrigger] = useState(0);
  const prevIdxRef = useRef(window.history.state?.idx);
  const acceptRequestInProgress = useRef(false);

  const navigate = useNavigate();

  const MAX_RETRIES = 5;

  const { PRODUCT } = getEnvironment();

  const { keyFactAceeptanceAPI } = new AfterOnboardAPI();

  useEffect(() => {
    const fetchAPI = async () => {
      try {
        const { keyFactStatementsAPI } = new AfterOnboardAPI();
        const res = await keyFactStatementsAPI();
        if (res && res?.statusCode === 200) {
          setData(res.data);
        } else if (resendTrigger < MAX_RETRIES) {
          setResendTrigger((prev) => prev + 1);
        }
      } catch (error) {
        if (resendTrigger < MAX_RETRIES) {
          setResendTrigger((prev) => prev + 1);
        }
      }
    };

    fetchAPI();
    googleEvent_LOJ167();
  }, [resendTrigger]);

  const handleKeyFactStatus = async (otp) => {
    if (acceptRequestInProgress.current) {
      return;
    }

    acceptRequestInProgress.current = true;

    setLoading(true);

    try {
      const { latitude, longitude } = await getGeolocation();
      const res = await keyFactAceeptanceAPI({
        otp,
        latitude,
        longitude,
      });

      const { kfsOtpRequest, kfsSuccess } = res?.data || {};

      if (res?.data?.data?.status && res?.data?.data?.redirectTo === "AADHAAR") {
        navigate("/enter-aadhar");
        return;
      }

      if (res?.data?.data?.status && res?.data?.data?.redirectTo === "VKYC") {
        navigate("/video-kyc");
        return;
      }

      if (KFS_OTP.kfsOtpRequest === kfsOtpRequest && KFS_OTP.kfsSuccess === kfsSuccess) {
        setOpenOtpModal(true);

        setLoading(false);
        return;
      }

      if (res?.statusCode === 203) {
        setApiResponseMsg(res.message);

        setLoading(false);
        return;
      }

      if (res?.data?.status === "1") {
        googleEvent_LOJ170();
        clevertap_key_fct_stat_accept_clk();
        clevertap_KFS_pl_screen_click();
        pixelEvents.disbursed();
        trackEvent({
          action: "KFS",
          category: "term-condition",
          label: "Accepted",
          value: 0,
        });

        if (window.fbq) {
          window.fbq("track", "Lead", {
            value: 1,
          });
        }

        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 1000);
        return;
      }

      Swal.fire({
        icon: "error",
        text: res?.data?.message || "Something went wrong. Please try again.",
      });

      setLoading(false);
    } catch (error) {
      console.error("KFS acceptance error:", error);

      setLoading(false);
      Swal.fire({
        icon: "error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      acceptRequestInProgress.current = false;
    }
  };

  useEffect(() => {
    clevertap_sv_key_fct_stat();
    clevertap_KFS_pl_screen();
    return () => {
      const currentIdx = window.history.state?.idx;
      if (prevIdxRef.current !== undefined && currentIdx < prevIdxRef.current) {
        clevertap_key_fct_stat_back_clk();
      }
    };
  }, []);

  return (
    <>
      <div className="wrapper auto-height">
        <div className="vertical-column-layout">
          <div className="vertical-column-top">
            <MainHeader title="Key Fact Statement" backPageName={"/bank-list"} />
            <div className={styles.termContent}>
              {data?.renderedHtml && (
                <div
                  className={styles.dynamic__content}
                  dangerouslySetInnerHTML={{
                    __html: data.renderedHtml,
                  }}
                />
              )}
            </div>
          </div>
          <div className={styles.acceptTermFooter}>
            {/* Disbursement Bank Account */}
            <div className={styles.disbursalBankAccount}>
              <h6>Disbursement Bank Account</h6>
              <div className={styles.bankList}>
                <div className={styles.bankLogo}>
                  <img src={data?.bankDetails?.bankLogo} alt="" />
                </div>
                <div>
                  <p>{data?.bankDetails?.accountNo}</p>
                  <span>{data?.bankDetails?.bankName}</span>
                </div>
              </div>
            </div>
            {/* Terms Checkbox */}
            <div className={styles.accept}>
              <div>
                <Checkbox
                  {...label}
                  checked={checkTerm}
                  disabled={loading}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 24,
                    },
                  }}
                  onChange={(e) => {
                    setCheckTerm(e.target.checked);
                    const clevertapEventName = e.target.checked ? "key_fct_stat_consent_chk" : "key_fct_stat_consent_uchk";
                    clevertap_key_fct_stat_consent({}, clevertapEventName);
                  }}
                />
              </div>
              <div>I acknowledge, that I have read and understood the Key Fact Statement and agreement mentioned therein and confirm that I hold the bank account mentioned above. I take full responsibility for the correctness of all provided information.</div>
            </div>
            {/* Accept Button */}
            <div className={styles.buttonWrapper}>
              <CustomButton
                floating={false}
                text={loading ? "Please wait..." : data?.start_vkyc === 1 ? "Start VKYC" : "I Accept"}
                disabled={!checkTerm || loading}
                onClick={async () => {
                  if (!acceptRequestInProgress.current) {
                    handleKeyFactStatus();
                  }

                  if (data?.start_vkyc === 1) {
                    await clevertap__KFS_screen();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      <OtpModal
        open={openOtpModal}
        handleClose={() => {
          setOpenOtpModal(false);
          // Ensure request is unlocked
          acceptRequestInProgress.current = false;
          setLoading(false);
        }}
        length={6}
        message={apiResponseMsg}
        resetMsg={setApiResponseMsg}
        handleSubmit={(otp) => {
          handleKeyFactStatus(otp);
        }}
      />
    </>
  );
}
